/**
 * A tiny, dependency-free fuzzy matcher for the Command Menu. Scores how well a
 * query subsequence-matches a target string, rewarding contiguous runs, matches at
 * word starts, and a leading/prefix match — enough for "jump to X" navigation without
 * pulling in a library. Returns `null` when the query is not a subsequence of the
 * target at all.
 */
export interface FuzzyResult {
  score: number;
  /** Indices in the target that matched — for optional highlight. */
  matches: number[];
}

export function fuzzyScore(query: string, target: string): FuzzyResult | null {
  const q = query.trim().toLowerCase();
  if (q === '') return { score: 1, matches: [] };
  const t = target.toLowerCase();

  let score = 0;
  let qi = 0;
  let prevMatch = -2;
  const matches: number[] = [];

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] !== q[qi]) continue;
    matches.push(ti);
    // Base point for the match.
    score += 1;
    // Contiguous run bonus.
    if (ti === prevMatch + 1) score += 2;
    // Word-boundary bonus (start of string, or after a separator).
    const before = ti === 0 ? ' ' : t[ti - 1];
    if (ti === 0) score += 3;
    else if (before === ' ' || before === '-' || before === '/' || before === '_') score += 2;
    prevMatch = ti;
    qi++;
  }

  if (qi < q.length) return null; // not all query chars consumed → no match
  // Reward a tighter match (shorter target, earlier start).
  score += Math.max(0, 6 - (matches[0] ?? 0));
  return { score, matches };
}

/** Rank+filter a list of searchable items against a query (best first). */
export function fuzzyFilter<T>(
  query: string,
  items: readonly T[],
  toText: (item: T) => string,
): T[] {
  if (query.trim() === '') return [...items];
  return items
    .map((item) => ({ item, res: fuzzyScore(query, toText(item)) }))
    .filter((x): x is { item: T; res: FuzzyResult } => x.res !== null)
    .sort((a, b) => b.res.score - a.res.score)
    .map((x) => x.item);
}
